import React, { useState, useEffect, useCallback } from "react";
import {
  selectedProductSchema,
  customerInfoSchema,
  customerAddressSchema,
  customerDropleadInfoSchema,
  headerDropLeadFromSchema,
  smeCustomerSchema,
} from "@/helpers/schema_validation";
import useSWR from "swr";
import axios from "axios";
import { STORAGE_KEY } from "@/helpers/constant/enum_data";
import apiMiddleware from "middlewares/apiMiddleware";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import { setUser } from "@sentry/nextjs";

const customerObj = {
  getUserId: "",
  getLocation: "",
  setLocation: () => {},
  selectedProduct: "",
  setSelectedProduct: () => {},
  getSelectedProduct: "",
  setCustomerInfo: () => {},
  getCustomerInfo: "",
  setSMEInfo: () => {},
  getSMEInfo: "",
  setCustomerAddress: () => {},
  getCustomerAddress: "",
  setHomeInfo: () => {},
  getHomeInfo: "",
  setUtm: () => {},
  setStaffCode: () => {},
  deleteHomeInfo: () => {},
};
const CustomerContext = React.createContext(customerObj);

export const CustomerContextProvider = (props) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customerInfo, setCustomerInfo] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [userUUID, setUserUUID] = useState();
  const [homeInfo, setHomeInfo] = useState({});
  const host = process.env.API_HOST;

  const fetchLocalStorage = async (key) => {
    let fetched = (await localStorage.getItem(key)) || "{}";
    fetched = JSON.parse(fetched);
    return fetched;
  };
  const getSession = async () => {
    const { data: result } = await axios
      .get(host + "/session", {
        withCredentials: true,
      })
      .catch(async (err) => {
        await axios.post(host + "/auth");
        return { data: {} };
      });
    return result;
  };
  const updateSession = async (value) => {
    const { data: result } = await axios.put(
      host + "/session",
      { userData: value },
      {
        withCredentials: true,
      }
    );
    return result;
  };
  const deleteLocalStorage = async (key) => {
    await localStorage.removeItem(key);
  };
  useEffect(async () => {
    const _homeInfo = (await fetchLocalStorage(STORAGE_KEY.HOME_INFO)) || {};
    const session = await getSession();
    if (!session) {
      return;
    }
    if (session.hasOwnProperty("uuid")) {
      setUserUUID(session.uuid);
    }
    Sentry.configureScope(function (scope) {
      scope.setUser({ id: session.uuid });
    });
  }, []);
  const getUserIdHandler = async () => {
    if (userUUID) {
      return userUUID;
    }
    const result = await getSession();
    setUserUUID(result.uuid);
    return result.uuid;
  };
  const getLocationHandler = async () => {
    const _location = await fetchLocalStorage(STORAGE_KEY.LOCATION);
    return _location;
  };
  const setLocationHandler = async (value) => {
    const stringifyObj = JSON.stringify(value);
    const { district, postcode, province, subDistrict } = value;
    await localStorage.setItem(STORAGE_KEY.LOCATION, stringifyObj);
    const _location = {
      [STORAGE_KEY.LOCATION]: { district, postcode, province, subDistrict },
    };
    await updateSession(_location);
    return Promise.resolve();
  };
  const getSelectedProductHandler = async () => {
    try {
      let _selectedProduct = selectedProduct;
      if (!_selectedProduct) {
        const result = await getSession();
        if (result.userData == null) {
          return Promise.resolve({});
        }
        _selectedProduct = result.userData[STORAGE_KEY.SELECTED_PRODUCT] || {};
        setSelectedProduct(_selectedProduct);
      }
      const getProduct = await selectedProductSchema().validate(
        _selectedProduct
      );
      return Promise.resolve(getProduct);
    } catch (err) {
      deleteLocalStorage(STORAGE_KEY.SELECTED_PRODUCT);
      return Promise.reject(err);
    }
  };
  const setSelectedProductHandler = async (value) => {
    try {
      await selectedProductSchema()
        .validate(value)
        .catch((err) => {
          throw new Error(err);
        });
      const _selectedProduct = { [STORAGE_KEY.SELECTED_PRODUCT]: value };
      await updateSession(_selectedProduct);
      setSelectedProduct(value);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const setCustomerInfoHandler = async (value, formType = "checkout") => {
    try {
      let validationSchema;
      switch (formType) {
        case "checkout":
          validationSchema = customerInfoSchema;
          break;
        case "dropleadHeader":
          validationSchema = headerDropLeadFromSchema;
          break;
        default:
          validationSchema = customerDropleadInfoSchema;
      }
      await validationSchema()
        .validate(value)
        .catch((e) => {
          throw e;
        });
      // const stringifyObj = JSON.stringify({ customerInfo: value });
      const _customerInfo = { [STORAGE_KEY.CUSTOMER_INFO]: value };
      await updateSession(_customerInfo);
      setCustomerInfo(value);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };
  const getCustomerInfoHandler = async () => {
    try {
      let _customerInfo = customerInfo;
      //Return if already have customerInfo in global state
      if (_customerInfo) {
        return Promise.resolve(_customerInfo);
      }
      const result = await getSession();
      if (result.userData == null) {
        return Promise.resolve({});
      }
      _customerInfo = result.userData[STORAGE_KEY.CUSTOMER_INFO] || {};
      setCustomerInfo(_customerInfo);
      return Promise.resolve(_customerInfo);
    } catch (err) {
      return Promise.reject(err.message);
    }
  };
  const setSMEInfoHandler = async (value) => {
    try {
      const _smeInfo = { [STORAGE_KEY.SME]: value };
      await updateSession(_smeInfo);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };
  const getSMEInfoHandler = async () => {
    try {
      const result = await getSession();
      if (result.userData == null) {
        return Promise.resolve({});
      }
      _smeInfo = result.userData[STORAGE_KEY.SME] || {};
      setCustomerInfo(_smeInfo);
      return Promise.resolve(_smeInfo);
    } catch (err) {
      return Promise.reject(err.message);
    }
  };
  const setCustomerAddressHandler = async (value) => {
    try {
      await customerAddressSchema().validate(value);
      const _customerAddress = { [STORAGE_KEY.CUSTOMER_ADDRESS]: value };
      await updateSession(_customerAddress);
      setCustomerAddress(value);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err.message);
    }
  };
  const getCustomerAddressHandler = async () => {
    try {
      let _customerAddress = customerAddress;
      //Return if already have customerAddress in global state
      if (_customerAddress) {
        return Promise.resolve(_customerAddress);
      }

      // _customerAddress = await fetchLocalStorage("customerAddress");
      const result = await getSession();
      if (result.userData == null) {
        return Promise.resolve({});
      }
      _customerAddress = result.userData[STORAGE_KEY.CUSTOMER_ADDRESS] || {};
      const getCustomerAddress = await customerAddressSchema().validate(
        _customerAddress
      );
      setCustomerAddress(_customerAddress);
      return Promise.resolve(getCustomerAddress);
    } catch (err) {
      return Promise.reject(err.message);
    }
  };

  const getHomeInfoHandler = async () => {
    try {
      const result = await getSession();
      if (result.userData == null) {
        return Promise.resolve({});
      }
      const _homeInfo = result.userData[STORAGE_KEY.HOME_INFO] || {};
      // const _homeInfo = await fetchLocalStorage(STORAGE_KEY.HOME_INFO);
      return Promise.resolve(_homeInfo);
    } catch (err) {
      return Promise.reject(err.message);
    }
  };
  const setHomeInfoHandler = async (value) => {
    try {
      //Update session
      const _homeInfoObject = { [STORAGE_KEY.HOME_INFO]: value };
      await updateSession(_homeInfoObject);

      //Update localstorage
      const _homeInfoLocal =
        (await fetchLocalStorage(STORAGE_KEY.HOME_INFO)) || {};
      const _homeInfo = { ..._homeInfoLocal, ...value };
      const stringifyHomeInfo = JSON.stringify(_homeInfo);
      setHomeInfo(_homeInfo);
      await localStorage.setItem(STORAGE_KEY.HOME_INFO, stringifyHomeInfo);
      return;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  const setUtmHandler = async (value) => {
    const session = await getSession();
    if (!session) {
      return;
    }
    const _utm = { [STORAGE_KEY.UTM]: value };
    const result = await updateSession(_utm).catch((err) => {
      throw new Error(err.message);
    });
    return result;
  };

  const setStaffCodeHandler = async (value) => {
    const _staffCode = { [STORAGE_KEY.STAFF_CODE]: value };
    const result = await updateSession(_staffCode).catch((err) => {
      throw new Error(err.message);
    });
    return result;
  };

  const deleteHomeInfoHandler = async () => {
    await deleteLocalStorage(STORAGE_KEY.HOME_INFO);
    await deleteLocalStorage(STORAGE_KEY.LOCATION);
  };

  return (
    <CustomerContext.Provider
      value={{
        getUserId: getUserIdHandler,
        getLocation: getLocationHandler,
        setLocation: setLocationHandler,
        selectedProduct: selectedProduct,
        setSelectedProduct: setSelectedProductHandler,
        getSelectedProduct: getSelectedProductHandler,
        setCustomerInfo: setCustomerInfoHandler,
        getCustomerInfo: getCustomerInfoHandler,
        setSMEInfo: setSMEInfoHandler,
        getSMEInfo: getSMEInfoHandler,
        setCustomerAddress: setCustomerAddressHandler,
        getCustomerAddress: getCustomerAddressHandler,
        setHomeInfo: setHomeInfoHandler,
        getHomeInfo: getHomeInfoHandler,
        setUtm: setUtmHandler,
        setStaffCode: setStaffCodeHandler,
        deleteHomeInfo: deleteHomeInfoHandler,
      }}
    >
      {props.children}
    </CustomerContext.Provider>
  );
};

export default CustomerContext;
